import { computed, inject, Injectable, signal } from '@angular/core';
import { SelectItem } from '../../../shared/interfaces/Iselect';
import { BomLineNumbering, StageGroup, TaxonomyLevels, VC_FG_BOMTYPE, vcFGComponent } from '../vas-configurator.constants';
import { GlobalService } from '../../../shared/services/global.service';
import { Calculation } from '../../../shared/services/calculation';
import { LoaderService } from '../../../shared/loader/loader.service';
import { EMPTY_GUID } from '../../../shared/constants/global';
import { ScopedComponentCorrelation, VcResolver } from './vc-resolver';
import { VcPayload } from './vc-payload';

@Injectable({
  providedIn: 'root',
})
export class VcDataService {
  public MaxDecimals = 4;
  public maxQtyDecimals = 2;
  public maxDecOnInpFocus = 2;
  public maxQtyValue = 999999.99;
  public minQtyValue = 0;
  maxMarginValue = 99.9999;
  minMarginValue = -999.9999;

  private _fgPartTypes = signal([]);
  readonly fgPartTypes = this._fgPartTypes.asReadonly();
  isLineShipUpdate = signal(false);
  isParentRollDownCall = signal(false);
  private _pendingApiCount = signal(0);

  private loaderService = inject(LoaderService);
  private globalService = inject(GlobalService);
  private calculator = inject(Calculation);
  private resolver = inject(VcResolver);
  private payload = inject(VcPayload);

  public hostOrigin = signal<any>(null);
  isNoMatchedShipLoc = signal(false);

  // ── Auth / session signals ──────────────────────────────────────────────────
  // set operation type ADD_FG or EDIT_FG
  private readonly _isAddFgMode = signal<boolean>(false);
  readonly isAddFgMode = this._isAddFgMode.asReadonly();

  // set sales order status
  private readonly _isSubmittedOrderStatus = signal<boolean>(false);
  readonly isSubmittedOrderStatus = this._isSubmittedOrderStatus.asReadonly();

  // set request from CPQ/SIR
  private readonly _hostRequest = signal<any>(null);
  readonly hostRequest = this._hostRequest.asReadonly();

  // true when Configurator is invoked by SIR — drives read-only field behavior
  readonly isConfiguratorReadOnly = computed<boolean>(
    () => this._hostRequest()?.sourceApplicationID === 'SIR',
  );
  public bomType = computed(() => this.vcFgDetails()?.BOMType);

  // true when Configurator is invoked directly by SIR — drives SIR-only date-field
  // behavior. Deliberately not derived from isConfiguratorReadOnly: that flag may grow
  // to cover other non-SIR read-only scenarios later and must stay decoupled from it.
  readonly isSirSource = computed<boolean>(() => this._hostRequest()?.sourceApplicationID === 'SIR');

  // set rounding factor from request
  readonly priceRoundingFactor = computed<number>(() => {
    const f = Number(this._hostRequest()?.priceRoundingFactor);
    return Number.isInteger(f) && f >= 0 && f <= this.MaxDecimals ? f : this.MaxDecimals;
  });

  // set rounding type from request
  readonly priceRoundingType = computed<string>(() => {
    const t = this._hostRequest()?.priceRoundingType;
    return t === 'Round Up' ? 'Round Up' : 'Traditional Rounding';
  });

  private readonly _hostFgPayload = signal<any>(null);
  readonly hostFgPayload = this._hostFgPayload.asReadonly();

  private shipLocationListMap: Map<any, any> = new Map();
  private salesLocationListMap: Map<any, any> = new Map();

  // ── BOM edit state ──────────────────────────────────────────────────────────

  private readonly _bomEditDialogConfirmed = signal<boolean>(false);
  readonly bomEditDialogConfirmed = this._bomEditDialogConfirmed.asReadonly();

  private readonly _hasBomStructuralChange = signal<boolean>(false);
  readonly hasBomStructuralChange = this._hasBomStructuralChange.asReadonly();

  private readonly _hasBOMEdited = signal<boolean>(false);
  readonly hasBOMEdited = this._hasBOMEdited.asReadonly();

  private _editSnapshot: {
    header: Record<string, any>;
    components: Map<string, Record<string, any>>;
    // FinishedGoodLineItemId -> the CPQ ref this row was paired to when the snapshot was taken.
    // A row's own GUID never changes, but its FIFO-derived CPQ ref can shift between snapshot and
    // submit time if a duplicate-PartNumber sibling is added/removed mid-edit-session — this keeps
    // the change-tracking diff anchored to this row's own original baseline regardless.
    refByLineItemId: Map<string, string>;
  } | null = null;

  confirmBomEditDialog(): void {
    this._bomEditDialogConfirmed.set(true);
  }

  markBomStructuralChange(): void {
    this._hasBomStructuralChange.set(true);
  }

  applyNewFgId(newFgId: string, newTransactiblePartNumber: string): void {
    sessionStorage.setItem('FINISHED_GOODS_ID', newFgId);
    this.updateFgDetails({
      FinishedGoodId: newFgId,
      TransactiblePartNumber: newTransactiblePartNumber,
      IsTPart: true,
    });
    this._hasBOMEdited.set(true);
  }

  resetBomEditState(): void {
    this._bomEditDialogConfirmed.set(false);
    this._hasBomStructuralChange.set(false);
    // _hasBOMEdited intentionally NOT reset — must persist until Submit
  }

  markBomAsEdited(): void {
    this._hasBOMEdited.set(true);
  }

  captureEditSnapshot(originalComponents?: any[]): void {
    const fgDetails = this.vcFgDetails();
    const vasComponents = originalComponents ?? this.vcFgComponents();
    const { vasIdToCpqComponent } = this.resolver.correlateCpqAndVasComponents(
      this.hostFgPayload()?.kitComponents,
      vasComponents,
    );

    const refByLineItemId = new Map<string, string>();
    const componentEntries: [string, Record<string, any>][] = [];
    for (const c of vasComponents) {
      const ref = vasIdToCpqComponent.get(c)?.saleOrderLineRefNumber;
      if (!ref) continue;
      componentEntries.push([ref, this.extractCompDiffValues(c)]);
      if (c.FinishedGoodLineItemId) {
        refByLineItemId.set(c.FinishedGoodLineItemId, ref);
      }
    }

    this._editSnapshot = {
      header: {
        ...this.extractHeaderDiffValues(fgDetails),
        shipLocation: fgDetails?.shipLocationId ?? null,
      },
      components: new Map(componentEntries),
      refByLineItemId,
    };
  }

  getSTKTypeFromCPQEnum(num: number): string | null {
    if (num === 8000002) return 'DS';
    if (num === 8000001) return 'IO';
    if (num === 8000000) return 'STK';
    return null;
  }

  // ── FG header signals ───────────────────────────────────────────────────────

  private readonly _vcFgDetails = signal<any>(null);
  readonly vcFgDetails = this._vcFgDetails.asReadonly();

  private readonly _vcFgCalculatedValues = signal<any>(null);
  readonly vcFgCalculatedValues = this._vcFgCalculatedValues.asReadonly();

  private readonly _doRollDownCalculation = signal<boolean>(false);
  readonly doRollDownCalculation = this._doRollDownCalculation.asReadonly();

  private readonly _invalidShipLocationComponents = signal(new Set());
  readonly invalidShipLocationComponents = this._invalidShipLocationComponents.asReadonly();

  lineAttachments = signal<{ [key: string]: any[] }>({});

  shipSelectionList = signal<SelectItem[]>([
    { text: 'No Delivery Date', id: 'No Delivery Date' },
    { text: 'Delivery By', id: 'Delivery By' },
    { text: 'Delivery Within', id: 'Delivery Within' },
    { text: 'Delivery On', id: 'Delivery On' },
    { text: 'Delivery After', id: 'Delivery After' },
  ]);

  // ── Setters ─────────────────────────────────────────────────────────────────

  setAddFgOperationType(isReadOnly: boolean) {
    this._isAddFgMode.set(isReadOnly);
  }

  setOrderStatus(isReadOnly: boolean) {
    this._isSubmittedOrderStatus.set(isReadOnly);
  }

  setHostRequest(request: any) {
    this._hostRequest.set(request);
  }

  setHostFgPayload(payload: any) {
    this._hostFgPayload.set(payload);
  }

  setFgDetails(fgDetail: any) {
    this._vcFgDetails.set(fgDetail);
  }

  updateFgDetails(fgDetail: any) {
    this._vcFgDetails.update((detail) => ({ ...detail, ...fgDetail }));
  }

  setFgCostPrice(data: any) {
    this._vcFgCalculatedValues.set(data);
  }

  updateFgCostPrice(data: any) {
    this._vcFgCalculatedValues.update((values) => ({ ...values, ...data }));
    this.refreshFgHeadersValues();
  }

  performRollDownCalculation(val: boolean) {
    this._doRollDownCalculation.set(val);
  }

  setShipLocations(shipLocations: any[]) {
    if (shipLocations.length > 0) {
      this.shipLocationListMap = new Map(
        shipLocations.map((item: any) => [item.Name.split(':')?.[0]?.trim(), item]),
      );
    } else {
      this.shipLocationListMap = new Map();
    }
  }

  setSalesLocations(salesLocations: any[]) {
    if (salesLocations.length > 0) {
      this.salesLocationListMap = new Map(
        salesLocations.map((item: any) => [item.SalesLocationNumber, item]),
      );
    } else {
      this.salesLocationListMap = new Map();
    }
  }

  setInvalidShipLocationComponents(ids: string[]) {
    this._invalidShipLocationComponents.set(new Set(ids));
  }

  // ── Component list signals ──────────────────────────────────────────────────

  private readonly _componentCostPriceValues = signal<any>(null);
  readonly componentCostPriceValues = this._componentCostPriceValues.asReadonly();

  private readonly _doRollUpCalculation = signal<boolean>(false);
  readonly doRollUpCalculation = this._doRollUpCalculation.asReadonly();

  private readonly _vcFgComponents = signal<vcFGComponent[]>([]);
  readonly vcFgComponents = this._vcFgComponents.asReadonly();

  readonly partStatusIssueComponents = computed(() =>
    this._vcFgComponents()
      .filter((c: any) => c.FinishedGoodLineItemId)
      .filter((c: any) => typeof c.PartStatus === 'string' && c.PartStatus !== 'Active'),
  );

  readonly allComponentsHaveApiData = computed(() => {
    if (!this._isAddFgMode()) return true;
    const components = this._vcFgComponents();
    const allDataLoaded = components
      .filter((c: any) => c.FinishedGoodLineItemId)
      .every((c: any) => c.PartJson && c.pricingInfo && c.InventoryJson);
    return allDataLoaded && this.partStatusIssueComponents().length === 0;
  });
  
  private restrictedInvCompIds = new Set<any>();

  setFgComponents(data: any) {
    this._vcFgComponents.set(data);
  }

  updateFgComponent(component: any) {
    this._vcFgComponents.update((components) =>
      components.map((cmp) =>
        cmp.FinishedGoodLineItemId === component.FinishedGoodLineItemId
          ? { ...cmp, ...component }
          : cmp,
      ),
    );
  }

  updateAllFgComponentsShipLocation(shipLocation: any, isResetStockTransfer = false): void {
    // handling the stock transfer reset, if we get null, we will use header ship location
    const vcFgDetailsShipLocation = this.vcFgDetails()?.ShipLocation?.id;
    const vcFgDetailsShipLocationId = this.getNumberFromLocationName(
      this.vcFgDetails()?.ShipLocation?.text,
    );
    this._vcFgComponents.update((components) =>
      components.map((cmp) => {
        const newShipLocation = shipLocation.shipLocationCode || vcFgDetailsShipLocationId;
        // A header-level Stock Transfer that actually moves this component to a different
        // ShipLocation invalidates any previously saved SupplierPOInfo (see SupplierPOInfoFetchKey,
        // "${SupplierSiteId}::${ShipLocation}") — otherwise it would silently pass the submit-time
        // completeness check and go out paired with the new location.
        const becameStale = !!cmp.SupplierPOInfo && newShipLocation !== cmp.ShipLocation;
        return {
          ...cmp,
          ShipLocationId: shipLocation.shipLocationId || vcFgDetailsShipLocation,
          ShipLocation: newShipLocation,
          ...(isResetStockTransfer ? { TransferFromShipLocation: null } : {}),
          ...(becameStale ? { SupplierPOInfo: null, SupplierPOInfoFetchKey: null } : {}),
        };
      }),
    );
  }

  updateNestedFgShipLocation(rowData: any): void {
    const fgNestedHeader = this.vcFgComponents().find(
      (c) => c.FinishedGoodLineItemId === rowData.FinishedGoodLineItemId,
    );
    this._vcFgComponents.update((components) =>
      components.map((cmp) => {
        if (cmp.FinishedGoodLineItemId === rowData.FinishedGoodLineItemId) {
          cmp.TransferFromShipLocation = rowData.TransferFromShipLocation;
          cmp.InventoryType = rowData.InventoryType;
          cmp.InventorySegment = rowData.InventorySegment;
          cmp.InventorySegmentType = rowData.InventorySegmentType;
        }
        if (cmp.ParentFinishedGoodLineItemId === rowData.FinishedGoodLineItemId) {
          const newShipLocation = rowData.TransferFromShipLocation || fgNestedHeader?.ShipLocation;
          // Same staleness rule as updateAllFgComponentsShipLocation — a nested-FG Stock
          // Transfer that actually moves this child to a different ShipLocation invalidates
          // any previously saved SupplierPOInfo.
          if (cmp.SupplierPOInfo && newShipLocation !== cmp.ShipLocation) {
            cmp.SupplierPOInfo = null;
            cmp.SupplierPOInfoFetchKey = null;
          }
          cmp.ShipLocationId =
            this.getShipLocationByCode(rowData.TransferFromShipLocation)?.Id ||
            fgNestedHeader?.ShipLocationId;
          cmp.ShipLocation = newShipLocation;
          cmp.TransferFromShipLocation = null;
        }
        return cmp;
      }),
    );
  }

  performRollUpCalculation(val: boolean) {
    this._doRollUpCalculation.set(val);
  }

  setComponentsCostPriceValues(values: Array<any>) {
    if (values && Array.isArray(values)) {
      this._componentCostPriceValues.set(this.buildCostPriceValuesMap(values));
      this.refreshComponentValues();
    }
  }

  // Same bulk cache rebuild as setComponentsCostPriceValues, but without the
  // refreshComponentValues()/performRollUpCalculation(true) tail — for callers (the rollup
  // pass itself) that already hold the freshly-resolved list and must not re-arm their own
  // trigger, which would otherwise loop forever.
  syncComponentsCostPriceValuesCache(values: Array<any>) {
    if (values && Array.isArray(values)) {
      this._componentCostPriceValues.set(this.buildCostPriceValuesMap(values));
    }
  }

  private buildCostPriceValuesMap(values: Array<any>): Map<string, any> {
    return new Map(
      values.map((item) => [
        item.FinishedGoodLineItemId,
        {
          Quantity: item.Quantity,
          UnitCost: this.globalService.formatPrice(item.UnitCost, true),
          UnitPrice: this.globalService.formatPrice(item.UnitPrice, true),
          ExtendedCost: this.globalService.formatPrice(item.ExtendedCost, true),
          ExtendedPrice: this.globalService.formatPrice(item.ExtendedPrice, true),
          GP: this.globalService.formatMargin(item.GP || item.BM, true, this.MaxDecimals),
          QuotedCost: this.globalService.formatPrice(item.QuotedCost, true),
        },
      ]),
    );
  }

  updateComponentsCostPriceValues(value: any) {
    this._componentCostPriceValues.update((current) => {
      const newMap = new Map(current);
      const prevValue = newMap.get(value.FinishedGoodLineItemId);
      if (prevValue) {
        newMap.set(value.FinishedGoodLineItemId, { ...prevValue, ...value });
      } else {
        newMap.set(value.FinishedGoodLineItemId, value);
      }
      return newMap;
    });
    this.refreshComponentValues();
  }

  updateOrderData() {
    const fgQty = Number(this.vcFgDetails()?.Quantity);
    if (!isNaN(fgQty)) {
      const components = this.vcFgComponents().map((comp) => {
        const unitCost = this.globalService.getOnlyValue(comp.UnitCost);
        const unitPrice = this.globalService.getOnlyValue(comp.UnitPrice);
        comp.orderQuantity = comp.Quantity
          ? this.calculator.preciseRound(comp.Quantity * fgQty, this.maxQtyDecimals)
          : null;
        comp.orderExtCost = this.calculator.preciseRound(
          comp.orderQuantity && unitCost ? comp.orderQuantity * unitCost : null,
          this.MaxDecimals,
        );
        comp.orderExtPrice = this.calculator.preciseRound(
          comp.orderQuantity && unitPrice ? comp.orderQuantity * unitPrice : null,
          this.MaxDecimals,
        );
        return comp;
      });
      this._vcFgComponents.set(components);
      this.performRollUpCalculation(true);
    }
  }

  deleteComponentsCostPriceValues(id: any) {
    this._componentCostPriceValues.update((current) => {
      const newMap = new Map(current);
      newMap.delete(id);
      return newMap;
    });
  }

  // ── Loader ──────────────────────────────────────────────────────────────────

  beginLoad() {
    if (this._pendingApiCount() === 0) {
      this.loaderService.setLoadingState.set(true);
    }
    this._pendingApiCount.update((n) => n + 1);
  }

  endLoad() {
    this._pendingApiCount.update((n) => Math.max(0, n - 1));
    if (this._pendingApiCount() === 0) {
      this.loaderService.setLoadingState.set(false);
    }
  }

  // ── Supplier / ship-to storage ──────────────────────────────────────────────

  supplierSiteAndShipObj = signal<{ siteList: any[] }>({ siteList: [] });
  shipTo = signal<{ attributeList: any[] }>({ attributeList: [] });

  public getFGPartTypes() {
    return this._fgPartTypes();
  }

  public setFGPartTypes(value: any) {
    this._fgPartTypes.set(value);
  }

  public getSupplierSiteAndShip() {
    return this.supplierSiteAndShipObj();
  }

  public setSupplierSiteAndShip(value: any) {
    this.supplierSiteAndShipObj.set(value);
  }

  public getLineShipUpdate() {
    return this.isLineShipUpdate();
  }

  public setShipTo(value: any) {
    this.shipTo.set(value);
  }

  public getShipTo() {
    return this.shipTo();
  }

  setRestrictedInvRows(id: string): void {
    this.restrictedInvCompIds.add(id)
  }
  
  getRestrictedInvRows(id: string) : boolean {
    return this.restrictedInvCompIds.has(id)
  }


  // ── Location utilities ──────────────────────────────────────────────────────

  updateMatchingShipLocations(shipLocationIds: string[] = []) {
    const sl =
      shipLocationIds.length > 0 ? shipLocationIds : this.vcFgDetails()?.ShipLocationIds || [];
    const selectedIdsSet = new Set(sl);
    const allShipLocations = this.globalService.getDataFromStorage('ship_location_list') || [];
    return allShipLocations
      .filter((location: any) => selectedIdsSet.has(location.Id))
      .map((item: any) => ({ id: item.Id, text: item.Name }))
      .sort((a: any, b: any) => a.text.localeCompare(b.text));
  }

  getAllShipLocations() {
    const allShipLocations = this.globalService.getDataFromStorage('ship_location_list') || [];
    return allShipLocations
      .map((item: any) => ({ id: item.Id, text: item.Name }))
      .sort((a: any, b: any) => a.text.localeCompare(b.text));
  }

  getOspComponentShipTo(): string | null {
    const ospVasComp = this.vcFgComponents().find(
      (c: any) => c.PartType === 'OSP' || c.PartType === 3,
    );
    if (!ospVasComp) return null;
    const { vasIdToCpqComponent } = this.resolver.correlateCpqAndVasComponents(
      this.hostFgPayload()?.kitComponents,
      this.vcFgComponents(),
    );
    return vasIdToCpqComponent.get(ospVasComp)?.componentShipTo ?? null;
  }

  getShipLocation(matchingShipLocations: any, shipLocationNumber: any) {
    if (!matchingShipLocations?.length) return null;
    for (const sl of matchingShipLocations) {
      if (this.resolver.getNumberFromLocationName(sl) === shipLocationNumber) return sl;
    }
    return null;
  }

  getDefaultShipLocation(matchingShipLocations: any[]): any | null {
    const defaultId = this.vcFgDetails()?.DefaultShipLocationId;
    if (!defaultId || !matchingShipLocations?.length) return null;
    return matchingShipLocations.find((sl: any) => sl.id === defaultId) ?? null;
  }

  getShipLocationByCode(code: string) {
    return this.shipLocationListMap.get(code) ?? null;
  }

  getSalesLocationByCode(code: string) {
    return this.salesLocationListMap.get(code) ?? null;
  }

  // ── Delegation wrappers for VcResolver ─────────────────────────────────────

  getFGlineItemShipTypes() {
    return this.resolver.getFGlineItemShipTypes();
  }

  resolveShipTypeToEnumValue(shipType: any): number | null {
    return this.resolver.resolveShipTypeToEnumValue(shipType);
  }

  resolvePartTypeToEnumValue(partType: any): number | null {
    return this.resolver.resolvePartTypeToEnumValue(partType, this.getFGPartTypes());
  }

  resolveqUomTypeToEnumValue(quomType: any): number | null {
    return this.resolver.resolveqUomTypeToEnumValue(quomType);
  }

  resolveUOMDescription(value: number | string | null | undefined): string | null {
    return this.resolver.resolveUOMDescription(value);
  }

  getNumberFromLocationName(location: SelectItem | string | null | undefined): string | null {
    return this.resolver.getNumberFromLocationName(location);
  }

  // Oracle's "Non-STK" has no direct ShipType entry (STK/IO/DS) — defaults to IO.
  resolveOrgShipType(stockNonStock: string | null | undefined): number | null {
    if (stockNonStock == null) return null;
    if (stockNonStock === 'Non-STK') {
      return this.resolveShipTypeToEnumValue('IO');
    }
    return this.resolveShipTypeToEnumValue(stockNonStock);
  }

  resolveShipLocationShipType(stockNonStock: string | null | undefined): string {
    return stockNonStock === 'STK' ? 'STK' : 'Non-STK';
  }

  // ── Delegation wrappers for VcPayload ──────────────────────────────────────

  transformBOMData(header: any, components: any) {
    return this.payload.transformBOMData(
      header,
      components,
      this.getSupplierSiteAndShip()?.siteList,
      this.getShipTo()?.attributeList,
      this.isAddFgMode(),
      this.getFGPartTypes(),
    );
  }

  buildSaveFgPayload(header: any, components: any[], isCreate: boolean) {
    return this.payload.buildSaveFgPayload(
      header,
      components,
      isCreate,
      this.getSupplierSiteAndShip()?.siteList,
      this.getShipTo()?.attributeList,
      this.isAddFgMode(),
      this.getFGPartTypes(),
    );
  }

  buildHostResponse(
    requestHeader: any,
    fgHeader: any,
    components: any[] = [],
    message?: string | null,
  ): any {
    const lineNumbering = this.assignBomLineNumbers(components, 1);
    return this.payload.buildHostResponse(
      requestHeader,
      fgHeader,
      components,
      this.getSupplierSiteAndShip()?.siteList,
      lineNumbering,
      message,
    );
  }

  generateSyntheticPartAndInventoryData(
    fgDetails: any,
    taxonomy?: TaxonomyLevels | null,
  ): {
    PartJson: string;
    InventoryJson: string;
    pricingInfo: string;
  } {
    return this.payload.generateSyntheticPartAndInventoryData(fgDetails, taxonomy);
  }

  generateSyntheticPartJsonForComponent(
    item: any,
    addLineItem: vcFGComponent,
    taxonomy?: TaxonomyLevels | null,
  ): string {
    return this.payload.generateSyntheticPartJsonForComponent(item, addLineItem, taxonomy);
  }

  mapFgDetailsFromRequestHeader(requstHeaders: any) {
    return this.payload.mapFgDetailsFromRequestHeader(requstHeaders);
  }

  mapFgDetailsDataFromCpq(fgDetails: any, cpQDetails: any): any {
    return this.payload.mapFgDetailsDataFromCpq(fgDetails, cpQDetails);
  }

  mapFgComponentsDataFromCpq(fgComponents: any[], cpqDetails: any): any[] {
    return this.payload.mapFgComponentsDataFromCpq(
      fgComponents,
      cpqDetails,
      this.MaxDecimals,
      this.getSupplierSiteAndShip()?.siteList ?? [],
    );
  }

  // ── buildEditFgResponse + private change-tracking (signal-coupled, stay here) ──

  buildEditFgResponse(fgHeader: any, message?: string | null): any {
    const requestHeader = this.hostRequest();
    const originalFg = this.hostFgPayload();
    const components = this.vcFgComponents();
    const isStructuralReplace = this._hasBOMEdited();

    if (fgHeader.shipType) {
      originalFg.shipType = fgHeader.shipType;
    }

    // Computed once and threaded through every consumer below, so BOM-diffing,
    // change-tracking, and merging all agree on the same CPQ<->VAS component correlation
    // for this submit — see VcResolver.correlateCpqAndVasComponents.
    const correlation = this.resolver.correlateCpqAndVasComponents(
      originalFg?.kitComponents,
      components,
    );

    const newRowNumbering = isStructuralReplace
      ? this.assignBomLineNumbersForStructuralEdit(components, originalFg, correlation)
      : null;

    const changes = this.computeFgChangeTracking(
      fgHeader,
      components,
      isStructuralReplace,
      newRowNumbering,
      correlation,
    );
    const hasChanges = Object.keys(changes).length > 0;

    const finishedGood = this.buildMergedFgWithChanges(
      originalFg,
      changes,
      isStructuralReplace,
      newRowNumbering,
      correlation,
    );
    finishedGood.fgTaskDetail = this.payload.buildHeaderTaskDetail(fgHeader, fgHeader?.BOMType ?? null);

    return {
      responseHeader: {
        operationType: requestHeader?.operationType,
        status: 'SUCCESS',
        statusMessage: message ?? null,
      },
      ...(isStructuralReplace
        ? { BOMChanges: this.buildBOMChanges(fgHeader, newRowNumbering, correlation) }
        : {}),
      finishedGood,
      changeTracking: hasChanges ? { finishedGood: changes } : null,
    };
  }

  private buildBOMChanges(
    fgHeader: any,
    newRowNumbering: Map<string, BomLineNumbering> | null,
    correlation: ScopedComponentCorrelation,
  ): any {
    const currentDetails = this.vcFgDetails();
    const vasComponents = this.vcFgComponents();
    const cpqComponents = this.hostFgPayload()?.kitComponents ?? [];
    const siteList = this.getSupplierSiteAndShip()?.siteList;

    const fgPricingInfo = this.resolver.parseJsonField(currentDetails?.pricingInfo);
    if (fgPricingInfo) {
      fgPricingInfo['supplierSite'] = this.payload.resolveSupplierNumber(
        currentDetails?.OSPSiteId,
        siteList,
      );
      fgPricingInfo['standardCost'] = this.globalService.getOnlyValue(currentDetails?.Cost);
      fgPricingInfo['sellPrice'] = this.globalService.getOnlyValue(currentDetails?.Price);
    }

    const modifyFG: Record<string, any> = {
      ...(currentDetails?.PartJson
        ? { productInfo: this.resolver.parseJsonField(currentDetails.PartJson) }
        : {}),
      ...(currentDetails?.InventoryJson
        ? { inventoryInfo: this.resolver.parseJsonField(currentDetails.InventoryJson) }
        : {}),
      ...(currentDetails?.OrgJson
        ? { orgAttributes: this.resolver.parseJsonField(currentDetails.OrgJson) }
        : {}),
      ...(currentDetails?.shipLocationShipType != null
        ? { shipLocationShipType: currentDetails.shipLocationShipType }
        : {}),
      ...(fgPricingInfo ? { pricingInfo: fgPricingInfo } : {}),
      ...(fgHeader.BOMType ? { fgBOMType: fgHeader.BOMType } : {}),
      ...(fgHeader.fgWorkInstructionURL != null
        ? { fgWorkInstructionURL: fgHeader.fgWorkInstructionURL }
        : {}),
      ...(fgHeader.fgLeadTimeInDays != null
        ? { fgLeadTimeInDays: this.resolver.toNumberOrNull(fgHeader.fgLeadTimeInDays) }
        : {}),
      ...(fgHeader.fgComponentShipTo != null
        ? { fgComponentShipTo: fgHeader.fgComponentShipTo }
        : {}),
      ...(fgHeader.Quantity != null ? { quantity: Number(fgHeader.Quantity) } : {}),
      ...(fgHeader.ShipSelction != null ? { shipSelection: fgHeader.ShipSelction } : {}),
      ...(fgHeader.EarliestAcceptDate != null
        ? { earliestAcceptDate: fgHeader.EarliestAcceptDate }
        : {}),
      ...(fgHeader.RequiredOnSiteDate != null
        ? { requiredOnsiteDate: fgHeader.RequiredOnSiteDate }
        : {}),
      ...(fgHeader.WarehouseDate != null ? { warehouseDate: fgHeader.WarehouseDate } : {}),
      ...(fgHeader.CustLineNum != null ? { customerLineNumber: fgHeader.CustLineNum } : {}),
      ...(fgHeader.TransferFromShipLocation != null
        ? { transferFromShipLocation: fgHeader.TransferFromShipLocation }
        : {}),
      ...(fgHeader.CutAssembly != null ? { fgCutAssemblyRequired: fgHeader.CutAssembly } : {}),
      ...(fgHeader.WorkOrderReleaseText != null
        ? { fgWorkOrderRelease: fgHeader.WorkOrderReleaseText }
        : {}),
    };
    if (modifyFG['productInfo'] && fgHeader.shipType != null) {
      modifyFG['productInfo'].shipType = fgHeader.shipType;
    }

    const deleteComponents = cpqComponents
      .filter((c: any) => !correlation.cpqRefToVasComponent.has(c.saleOrderLineRefNumber ?? ''))
      .map((c: any) => ({ saleOrderLineRefNumber: c.saleOrderLineRefNumber }));

    const bomType = fgHeader.BOMType ?? null;
    const addComponents = vasComponents
      .filter((c: any) => !correlation.vasIdToCpqComponent.has(c))
      .map((c: any) => {
        const pricingInfo = this.resolver.parseJsonField(c.pricingInfo);
        if (pricingInfo) {
          pricingInfo['supplierSite'] = this.payload.resolveSupplierNumber(
            c.SupplierSiteId,
            siteList,
          );
          pricingInfo['standardCost'] = this.globalService.getOnlyValue(c.UnitCost);
          pricingInfo['sellPrice'] = this.globalService.getOnlyValue(c.UnitPrice);
          pricingInfo['enteredCost'] = this.resolver.resolveEnteredCostOverride(pricingInfo);
        }
        const numbering = newRowNumbering?.get(c.FinishedGoodLineItemId ?? '');
        return {
          transactablePartNumber: c.PartNumber ?? null,
          ...this.extractCompDiffValues(c),
          ...(numbering
            ? {
              salesOrderLineNumber: numbering.salesOrderLineNumber,
              parentSalesOrderLineNumber: numbering.parentSalesOrderLineNumber,
              bomLevel: numbering.bomLevel,
              parentSaleOrderLineRefNumber: numbering.parentSaleOrderLineRefNumber ?? null,
            }
            : {}),
          productInfo: this.resolver.parseJsonField(c.PartJson),
          ...(c.InventoryJson
            ? { inventoryInfo: this.resolver.parseJsonField(c.InventoryJson) }
            : {}),
          ...(c.OrgJson ? { orgAttributes: this.resolver.parseJsonField(c.OrgJson) } : {}),
          ...(pricingInfo ? { pricingAPIDataCall: true, pricingInfo } : {}),
          fgTaskDetail: this.payload.buildComponentTaskDetail(c, bomType),
        };
      });

    return { ModifyFG: modifyFG, DeleteComponents: deleteComponents, AddComponents: addComponents };
  }

  private buildMergedFgWithChanges(
    originalFg: any,
    changes: Record<string, any>,
    isStructuralReplace = false,
    newRowNumbering: Map<string, BomLineNumbering> | null = null,
    correlation?: ScopedComponentCorrelation,
  ): any {
    correlation ??= this.resolver.correlateCpqAndVasComponents(
      originalFg?.kitComponents,
      this.vcFgComponents(),
    );
    const bomType = this.vcFgDetails()?.BOMType ?? null;
    if (!isStructuralReplace) {
      const compChangesMap = new Map<string, any>(
        (changes['kitComponents'] ?? []).map((c: any) => [c.saleOrderLineRefNumber, c]),
      );
      const { kitComponents: _, ...headerChanges } = changes;
      const fgDetails = this.vcFgDetails();

      return {
        ...originalFg,
        ...headerChanges,
        shipLocationShipType: fgDetails?.shipLocationShipType ?? 'Non-STK',
        orgAttributes: this.resolver.parseJsonField(fgDetails?.OrgJson),
        kitComponents: (originalFg?.kitComponents ?? []).map((comp: any) => {
          const vasComp = correlation.cpqRefToVasComponent.get(comp.saleOrderLineRefNumber ?? '');
          const compOverride = {
            shipLocationShipType: vasComp?.shipLocationShipType ?? 'Non-STK',
            orgAttributes: this.resolver.parseJsonField(vasComp?.OrgJson),
            inventoryInfo: this.resolver.parseJsonField(vasComp?.InventoryJson),
            fgTaskDetail: this.payload.buildComponentTaskDetail(vasComp ?? {}, bomType),
          };
          const compChanges = compChangesMap.get(comp.saleOrderLineRefNumber);
          if (!compChanges) return { ...comp, ...compOverride };
          const { productInfo: changedProductInfo, ...restChanges } = compChanges;
          return {
            ...comp,
            ...restChanges,
            ...compOverride,
            productInfo: changedProductInfo
              ? { ...(comp.productInfo ?? {}), ...changedProductInfo }
              : comp.productInfo,
          };
        }),
      };
    }

    const compChangesMap = new Map<string, any>(
      (changes['kitComponents'] ?? []).map((c: any) => [c.saleOrderLineRefNumber, c]),
    );
    const { kitComponents: _, ...headerChanges } = changes;
    const siteList = this.getSupplierSiteAndShip()?.siteList;

    const kitComponents: any[] = [];

    for (const vasComp of this.vcFgComponents() as any[]) {
      const cpqComp: any = correlation.vasIdToCpqComponent.get(vasComp);
      if (cpqComp) {
        const compChanges = compChangesMap.get(cpqComp.saleOrderLineRefNumber ?? '');
        const hasChange = !!compChanges && Object.keys(compChanges).length > 1;
        const { productInfo: changedProductInfo, ...restChanges } = compChanges ?? {};
        const mergedProductInfo = changedProductInfo
          ? { ...(cpqComp.productInfo ?? {}), ...changedProductInfo }
          : cpqComp.productInfo;
        const pricingInfoPresent = !!(cpqComp.pricingInfo || compChanges?.pricingInfo);
        kitComponents.push({
          ...cpqComp,
          ...(hasChange ? restChanges : {}),
          shipLocationShipType: vasComp.shipLocationShipType ?? 'Non-STK',
          orgAttributes: this.resolver.parseJsonField(vasComp.OrgJson),
          productInfo: mergedProductInfo,
          ...(vasComp.InventoryJson
            ? { inventoryInfo: this.resolver.parseJsonField(vasComp.InventoryJson) }
            : {}),
          ...(pricingInfoPresent ? { pricingAPIDataCall: true } : {}),
          fgTaskDetail: this.payload.buildComponentTaskDetail(vasComp, bomType),
        });
      } else {
        const pricingInfo = this.resolver.parseJsonField(vasComp.pricingInfo);
        if (pricingInfo) {
          if (vasComp.SupplierSiteId != null) pricingInfo['supplierSite'] = vasComp.SupplierSiteId;
          pricingInfo['standardCost'] = this.globalService.getOnlyValue(vasComp.UnitCost);
          pricingInfo['sellPrice'] = this.globalService.getOnlyValue(vasComp.UnitPrice);
          pricingInfo['enteredCost'] = this.resolver.resolveEnteredCostOverride(pricingInfo);
        }
        const numbering = newRowNumbering?.get(vasComp.FinishedGoodLineItemId ?? '');
        kitComponents.push({
          transactablePartNumber: vasComp.PartNumber ?? null,
          ...this.extractCompDiffValues(vasComp),
          ...(numbering
            ? {
              salesOrderLineNumber: numbering.salesOrderLineNumber,
              parentSalesOrderLineNumber: numbering.parentSalesOrderLineNumber,
              bomLevel: numbering.bomLevel,
              parentSaleOrderLineRefNumber: numbering.parentSaleOrderLineRefNumber ?? null,
            }
            : {}),
          productInfo: this.resolver.parseJsonField(vasComp.PartJson),
          ...(vasComp.InventoryJson
            ? { inventoryInfo: this.resolver.parseJsonField(vasComp.InventoryJson) }
            : {}),
          orgAttributes: this.resolver.parseJsonField(vasComp.OrgJson),
          ...(pricingInfo ? { pricingAPIDataCall: true, pricingInfo } : {}),
          fgTaskDetail: this.payload.buildComponentTaskDetail(vasComp, bomType),
        });
      }
    }

    return {
      ...originalFg,
      ...headerChanges,
      shipLocationShipType: this.vcFgDetails()?.shipLocationShipType ?? 'Non-STK',
      orgAttributes: this.resolver.parseJsonField(this.vcFgDetails()?.OrgJson),
      kitComponents,
    };
  }

  private computeFgChangeTracking(
    fgDetails: any,
    vasComponents: any[],
    isStructuralReplace = false,
    newRowNumbering: Map<string, BomLineNumbering> | null = null,
    correlation?: ScopedComponentCorrelation,
  ): Record<string, any> {
    const snapshot = this._editSnapshot;
    const result: Record<string, any> = {};
    const siteList = this.getSupplierSiteAndShip()?.siteList;

    result['saleOrderLineRefNumber'] = this.hostFgPayload()?.saleOrderLineRefNumber ?? null;

    const currentHeader = this.extractHeaderDiffValues(fgDetails);
    for (const key of Object.keys(currentHeader)) {
      if (!this.resolver.editValuesEqual(snapshot?.header[key], currentHeader[key])) {
        result[key] = currentHeader[key];
      }
    }
    if (
      result['unitCost'] !== undefined ||
      result['unitPrice'] !== undefined ||
      result['gpPercentage'] !== undefined
    ) {
      const cost = this.globalService.getOnlyValue(fgDetails?.Cost);
      const price = this.globalService.getOnlyValue(fgDetails?.Price);
      result['gpAmount'] =
        cost != null && price != null
          ? this.calculator.preciseRound(price - cost, this.MaxDecimals)
          : null;
    }
    if ('PartJson' in fgDetails && fgDetails.PartJson) {
      result['productInfo'] = this.resolver.parseJsonField(fgDetails.PartJson);
    }
    if ('pricingInfo' in fgDetails && fgDetails.pricingInfo) {
      const parsedHeaderPricingInfo = this.resolver.parseJsonField(fgDetails.pricingInfo);
      parsedHeaderPricingInfo['standardCost'] = this.globalService.getOnlyValue(fgDetails?.Cost);
      parsedHeaderPricingInfo['sellPrice'] = this.globalService.getOnlyValue(fgDetails?.Price);
      result['pricingInfo'] = parsedHeaderPricingInfo;
      result['pricingAPIDataCall'] = true;
    }
    if ('InventoryJson' in fgDetails && fgDetails.InventoryJson) {
      result['inventoryInfo'] = this.resolver.parseJsonField(fgDetails.InventoryJson);
    }
    if ('OrgJson' in fgDetails && fgDetails.OrgJson) {
      result['orgAttributes'] = this.resolver.parseJsonField(fgDetails.OrgJson);
    }
    if ('BOMType' in fgDetails && fgDetails.BOMType) {
      result['fgBOMType'] = fgDetails.BOMType;
    }

    correlation ??= this.resolver.correlateCpqAndVasComponents(
      this.hostFgPayload()?.kitComponents,
      vasComponents,
    );

    const bomType = fgDetails?.BOMType ?? null;
    const componentChanges: any[] = [];
    for (const vasComp of vasComponents) {
      const ref = correlation.vasIdToCpqComponent.get(vasComp)?.saleOrderLineRefNumber;
      if (!ref) {
        if (isStructuralReplace) {
          const addedPricingInfo = this.resolver.parseJsonField(vasComp.pricingInfo);
          if (addedPricingInfo) {
            addedPricingInfo['supplierSite'] = this.payload.resolveSupplierNumber(
              vasComp.SupplierSiteId,
              siteList,
            );
            addedPricingInfo['standardCost'] = this.globalService.getOnlyValue(vasComp.UnitCost);
            addedPricingInfo['sellPrice'] = this.globalService.getOnlyValue(vasComp.UnitPrice);
            addedPricingInfo['enteredCost'] =
              this.resolver.resolveEnteredCostOverride(addedPricingInfo);
          }
          const numbering = newRowNumbering?.get(vasComp.FinishedGoodLineItemId ?? '');
          componentChanges.push({
            transactablePartNumber: vasComp.PartNumber ?? null,
            ...this.extractCompDiffValues(vasComp),
            ...(numbering
              ? {
                salesOrderLineNumber: numbering.salesOrderLineNumber,
                parentSalesOrderLineNumber: numbering.parentSalesOrderLineNumber,
                bomLevel: numbering.bomLevel,
                parentSaleOrderLineRefNumber: numbering.parentSaleOrderLineRefNumber ?? null,
              }
              : {}),
            ...(addedPricingInfo
              ? { pricingAPIDataCall: true, pricingInfo: addedPricingInfo }
              : {}),
            productInfo: this.resolver.parseJsonField(vasComp.PartJson),
            ...(vasComp.InventoryJson
              ? { inventoryInfo: this.resolver.parseJsonField(vasComp.InventoryJson) }
              : {}),
            ...(vasComp.OrgJson
              ? { orgAttributes: this.resolver.parseJsonField(vasComp.OrgJson) }
              : {}),
            fgTaskDetail: this.payload.buildComponentTaskDetail(vasComp, bomType),
          });
        }
        continue;
      }

      // Prefer this row's own snapshot-time ref (stable across the edit session, keyed by its own
      // GUID) over the live-recomputed `ref` above — which is re-derived from scratch on every
      // call and can point at a different sibling if a duplicate-PartNumber row was added/removed
      // since the snapshot was taken. Falls back to the live `ref` for rows not present at
      // snapshot time (unreachable here in practice, since new rows never have a `ref` at all and
      // are handled by the `!ref` branch above).
      const snapshotRef = snapshot?.refByLineItemId.get(vasComp.FinishedGoodLineItemId ?? '') ?? ref;
      const snapshotComp = snapshot?.components.get(snapshotRef) ?? {};
      const currentComp = this.extractCompDiffValues(vasComp);

      const compDiff: Record<string, any> = {};
      for (const key of Object.keys(currentComp)) {
        // spaConfiguration is a freshly-built object on every call — compared separately below via
        // field-level equality, since reference equality would always report it as changed.
        if (key === 'spaConfiguration') continue;
        if (!this.resolver.editValuesEqual(snapshotComp[key], currentComp[key])) {
          compDiff[key] = currentComp[key];
        }
      }
      if (!this.resolver.spaConfigurationEqual(snapshotComp['spaConfiguration'], currentComp['spaConfiguration'])) {
        compDiff['spaConfiguration'] = currentComp['spaConfiguration'];
      }
      if ('pricingInfo' in vasComp) {
        compDiff['pricingAPIDataCall'] = true;
        const parsedPricingInfo = vasComp.pricingInfo
          ? typeof vasComp.pricingInfo === 'string'
            ? JSON.parse(vasComp.pricingInfo)
            : vasComp.pricingInfo
          : null;
        if (parsedPricingInfo) {
          parsedPricingInfo['supplierSite'] = this.payload.resolveSupplierNumber(
            vasComp.SupplierSiteId,
            siteList,
          );
          parsedPricingInfo['standardCost'] = this.globalService.getOnlyValue(vasComp.UnitCost);
          parsedPricingInfo['sellPrice'] = this.globalService.getOnlyValue(vasComp.UnitPrice);
          parsedPricingInfo['enteredCost'] =
            this.resolver.resolveEnteredCostOverride(parsedPricingInfo);
        }
        compDiff['pricingInfo'] = parsedPricingInfo;
      }
      if ('PartJson' in vasComp && vasComp.PartJson) {
        const partJson =
          typeof vasComp.PartJson === 'string' ? JSON.parse(vasComp.PartJson) : vasComp.PartJson;
        compDiff['productInfo'] = { ...partJson };
      }
      if ('OrgJson' in vasComp && vasComp.OrgJson) {
        compDiff['orgAttributes'] = this.resolver.parseJsonField(vasComp.OrgJson);
      }
      if ('InventoryJson' in vasComp && vasComp.InventoryJson) {
        compDiff['inventoryInfo'] = this.resolver.parseJsonField(vasComp.InventoryJson);
      }
      const isOspComponent = vasComp.PartType === 3 || vasComp.PartType === 'OSP';
      if (isOspComponent) {
        compDiff['componentShipTo'] = this.vcFgDetails()?.OSPShipToId ?? null;
      }
      if (this.resolver.isNestedFgComponent(vasComp)) {
        const fgHeaderDetails = vasComp.FGHeaderDetails ?? {};
        const typeCodeList = this.globalService.getDataFromStorage('type_code_descriptions');
        compDiff['fgBOMType'] = this.resolver.resolveBomTypeKey(fgHeaderDetails);
        compDiff['fgWorkInstructionURL'] = fgHeaderDetails.fgWorkInstructionURL ?? null;
        compDiff['fgLeadTimeInDays'] = this.resolver.toNumberOrNull(
          fgHeaderDetails.FGLeadTimeInDays,
        );
        compDiff['fgWorkOrderRelease'] = this.resolver.resolveWorkOrderReleaseText(
          fgHeaderDetails.WorkOrderRelease,
          typeCodeList,
        );
        compDiff['fgComponentShipTo'] = this.resolver.resolveShipToAccNum(
          fgHeaderDetails.OSPShipToId,
          this.getShipTo()?.attributeList,
        );
      }

      const hasCompChange = Object.keys(compDiff).length > 0;
      if (hasCompChange || isStructuralReplace) {
        if (hasCompChange) {
          if (
            compDiff['unitCost'] !== undefined ||
            compDiff['unitPrice'] !== undefined ||
            compDiff['gpPercentage'] !== undefined
          ) {
            const cost = this.globalService.getOnlyValue(vasComp.UnitCost);
            const price = this.globalService.getOnlyValue(vasComp.UnitPrice);
            compDiff['gpAmount'] =
              cost != null && price != null
                ? this.calculator.preciseRound(price - cost, this.MaxDecimals)
                : null;
          }
        }
        componentChanges.push({
          saleOrderLineRefNumber: ref,
          ...compDiff,
        });
      }
    }

    if (componentChanges.length > 0 || isStructuralReplace) {
      result['kitComponents'] = componentChanges;
    }
    return result;
  }

  private extractHeaderDiffValues(fgDetails: any): Record<string, any> {
    return {
      quantity:
        fgDetails?.Quantity != null && !isNaN(Number(fgDetails.Quantity))
          ? Number(fgDetails.Quantity)
          : null,
      unitCost: fgDetails?.Cost,
      unitPrice: fgDetails?.Price,
      averageCost: fgDetails?.AverageCost,
      gpPercentage: this.resolver.normalizeGP(fgDetails?.GP ?? fgDetails?.GPPercentage),
      shipType: this.resolver.resolveShipTypeDescription(fgDetails?.ShipType),
      shipSelection: fgDetails?.ShipSelction,
      shipLocation: this.resolver.getNumberFromLocationName(fgDetails?.ShipLocation),
      earliestAcceptDate: fgDetails?.EarliestAcceptDate,
      requiredOnsiteDate: fgDetails?.RequiredOnSiteDate,
      warehouseDate: fgDetails?.WarehouseDate,
      customerLineNumber: fgDetails?.CustLineNum,
      transferFromShipLocation: fgDetails?.TransferFromShipLocation,
      fgLeadTimeInDays: this.resolver.toNumberOrNull(fgDetails?.FGLeadTimeInDays),
      fgWorkReleaseDate: fgDetails?.FgWorkReleaseDate,
      fgWorkOrderRelease: fgDetails?.WorkOrderRelease,
      fgCutAssemblyRequired: fgDetails?.CutAssembly,
      supplierSite: fgDetails?.OSPSiteId,
      noteVasWorkInstruction: fgDetails?.noteVasWorkInstruction,
      shipLocationShipType: fgDetails?.shipLocationShipType ?? 'Non-STK',
    };
  }

  private extractCompDiffValues(comp: any): Record<string, any> {
    return {
      kitConfigQty:
        comp.Quantity != null && !isNaN(Number(comp.Quantity)) ? Number(comp.Quantity) : null,
      quantity: comp.orderQuantity ?? null,
      unitCost: this.globalService.getOnlyValue(comp.UnitCost),
      unitPrice: this.globalService.getOnlyValue(comp.UnitPrice),
      averageCost: this.globalService.getOnlyValue(comp.AverageCost),
      gpPercentage: this.resolver.normalizeGP(comp.GP ?? comp.BM),
      shipType: this.resolver.resolveShipTypeDescription(comp.ShipType),
      shipLocation: this.resolver.getNumberFromLocationName(comp.ShipLocation),
      inventoryType: this.resolver.resolveInventoryTypeDescription(comp.InventoryType),
      inventorySegment: comp.InventorySegment ?? null,
      inventorySegmentType: comp.InventorySegmentType ?? null,
      supplierSite: comp.SupplierSiteId,
      supplierQuoteNumber: comp.SupplierQuoteNumber,
      supplierQuoteCost: this.globalService.getOnlyValue(comp.QuotedCost),
      supplierQuoteValidFrom: comp.QuotedEffectiveDate,
      supplierQuoteValidTill: comp.SupplierQuoteValidTillDate,
      supplierQuoteUOM: this.resolver.resolveUOMDescription(comp.QuotedUOM),
      transferFromShipLocation: comp.TransferFromShipLocation,
      shipLocationShipType: comp.shipLocationShipType ?? 'Non-STK',
      // Reshaped to the CPQ wire contract (camelCase, single selected contact) — never the raw
      // internal SupplierPOInfo, which carries the full Contacts[] directory (VAS-453).
      supplierInfo: this.payload.buildSupplierInfoPayload(
        comp,
        this.getSupplierSiteAndShip()?.siteList ?? [],
      ),
      spaConfiguration: this.payload.buildSpaConfigurationPayload(comp),
    };
  }

  // ── Misc ────────────────────────────────────────────────────────────────────

  getResponseHeader(type: string, req: any, message?: string | null) {
    return {
      responseHeader: {
        operationType: req?.operationType,
        status: type,
        statusMessage: message,
      },
    };
  }

  getSTKShipTypeFromCPQEnum(num: number): string | null {
    return this.getSTKTypeFromCPQEnum(num);
  }

  private refreshComponentValues() {
    const updatedList = this.vcFgComponents().map((item) => {
      const costPrices = this.componentCostPriceValues().get(item.FinishedGoodLineItemId);
      return costPrices ? { ...item, ...costPrices } : item;
    });
    this.setFgComponents(updatedList);
    this.updateOrderData();
    this.performRollUpCalculation(true);
  }

  private refreshFgHeadersValues() {
    const fgDetailsValues = this.vcFgCalculatedValues();
    const fgDetails = this.vcFgDetails();
    this.updateFgDetails({ ...fgDetails, ...fgDetailsValues });
  }

  private getLocationCodeFromId(id: string | null | undefined): string | null {
    if (!id) return null;
    const allShipLocations = this.globalService.getDataFromStorage('ship_location_list') || [];
    const match = allShipLocations.find((loc: any) => loc.Id === id);
    return match?.Name?.split(':')?.[0] ?? null;
  }

  public buildNestedLineItems(items: vcFGComponent[]): vcFGComponent[] {
    const validItems = items.filter((item) => item.FinishedGoodLineItemId && !item.StageGroupNumber);
    const map = new Map<string, vcFGComponent>(
      validItems.map((item) => [item.FinishedGoodLineItemId!, { ...item, children: []}]),
    );

    const roots: vcFGComponent[] = [];
    for (const item of map.values()) {
      const pid = item.ParentFinishedGoodLineItemId;
      if (pid && map.has(pid)) {
        map.get(pid)!.children!.push(item);
      } else {
        roots.push(item);
      }
    }

     //assign line number for nested sequence
    const processNodesRecursive = (nodes: vcFGComponent[], parentValue = '') => {
      // Sort current level nodes by LineNumber ascending
      nodes.sort((a, b) => Number(a.LineNumber ?? 0) - Number(b.LineNumber ?? 0));

      nodes.forEach((node, index) => {
        const currentSequence = index + 1; // 1, 2 ... 9, 10, 11
        const calculatedLineStr =
          parentValue === '' ? `${currentSequence}` : `${parentValue}.${currentSequence}`;

        //  "2.10", "3.10"
        (node as any).LineNumber = calculatedLineStr;

        // Recurse down into child elements if they exist
        if (node.children?.length) {
          processNodesRecursive(node.children, calculatedLineStr);
        }
      });
    };

    processNodesRecursive(roots);

    return roots;
  }

  // Display-only grouping of staged components for RVA BOMs — never written back into
  // vcFgComponents(). Each child is a clone, so callers may freely assign display fields
  // (e.g. LineNumber) without mutating the canonical signal state.
  public buildStageGroups(items: vcFGComponent[]): StageGroup[] {
    if (this.bomType() !== 'RepeatableValueAdd') {
      return [];
    }
    const staged = items.filter((item) => item.StageGroupNumber);
    const groups = new Map<number, StageGroup>();
    for (const item of staged) {
      const stageGroupNumber = item.StageGroupNumber;
      if (!groups.has(stageGroupNumber)) {
        groups.set(stageGroupNumber, {
          StageGroupNumber: stageGroupNumber,
          StageGroupName: this.stageTitle(stageGroupNumber),
          StageDependenciesNames: this.buildStageDependencies(item.StageDependencies),
          children: [],
        });
      }
      groups.get(stageGroupNumber)!.children.push({ ...item });
    }

    const sortedGroups = Array.from(groups.values()).sort(
      (a, b) => a.StageGroupNumber - b.StageGroupNumber,
    );
    let lineNumber = 1;
    for (const group of sortedGroups) {
      for (const child of group.children) {
        child.LineNumber = lineNumber++;
      }
    }
    return sortedGroups;
  }

  // Merges the corrected flat LineNumber from buildStageGroups() back onto real staged
  // components. Called explicitly at load time — never from inside a computed — so the
  // write is a deliberate, one-time commit rather than a side effect of rendering.
  public applyStageLineNumbers(items: vcFGComponent[]): vcFGComponent[] {
    const groups = this.buildStageGroups(items);
    if (!groups.length) {
      return items;
    }
    const correctedById = new Map<string, number>();
    for (const group of groups) {
      for (const child of group.children) {
        if (child.FinishedGoodLineItemId) {
          correctedById.set(child.FinishedGoodLineItemId, child.LineNumber as number);
        }
      }
    }
    return items.map((item) =>
      item.FinishedGoodLineItemId && correctedById.has(item.FinishedGoodLineItemId)
        ? { ...item, LineNumber: correctedById.get(item.FinishedGoodLineItemId)! }
        : item,
    );
  }

  buildStageDependencies(stageDependecies: any): string | null {
    if (!stageDependecies?.length) {
      return 'NA';
    }
    return stageDependecies.map((stage:any) =>
       this.stageTitle(stage)).join(',');
  }

  stageTitle(stageId: number | null): string | null {
    if (stageId === null) {
      return null;
    }
    return `STAGE${stageId.toString().padStart(3, '0')}`;
  }


  // Inverse of buildNestedLineItems — collects each node + descendants back into a flat array,
  // stripping the transient `children` key so it can be written back into vcFgComponents.
  public flattenTree(nodes: vcFGComponent[]): vcFGComponent[] {
    const flat: vcFGComponent[] = [];
    const visit = (items: vcFGComponent[]) => {
      items.forEach((item) => {
        const { children, ...rest } = item;
        flat.push(rest as vcFGComponent);
        if (children?.length) {
          visit(children);
        }
      });
    };
    visit(nodes);
    return flat;
  }

  // ── BOM hierarchy numbering (salesOrderLineNumber/parentSalesOrderLineNumber/bomLevel) ──

  public assignBomLineNumbers(
    components: vcFGComponent[],
    startingLineNumber = 1,
  ): { root: BomLineNumbering; componentMap: Map<string, BomLineNumbering> } {
    // Staged (RVA) components carry a truthy StageGroupNumber, so buildNestedLineItems's own
    // filter excludes them from its tree — append them as flat roots so they get numbered by
    // the exact same walk as any other component (matches how triggerRollUpCalculations/
    // triggerRollDownCalculations in VcContainer already treat them: pulled out, then rejoined
    // as flat top-level siblings rather than left out of the calculation entirely).
    const stagedComponents = components
      .filter((item) => item.StageGroupNumber)
      .sort((a, b) => Number(a.LineNumber ?? 0) - Number(b.LineNumber ?? 0));
    const roots = [...this.buildNestedLineItems(components), ...stagedComponents];
    const componentMap = new Map<string, BomLineNumbering>();
    let nextNumber = startingLineNumber;

    const root: BomLineNumbering = {
      salesOrderLineNumber: nextNumber++,
      parentSalesOrderLineNumber: null,
      bomLevel: 0,
    };

    const walk = (nodes: vcFGComponent[], parent: BomLineNumbering) => {
      for (const node of nodes) {
        const numbering: BomLineNumbering = {
          salesOrderLineNumber: nextNumber++,
          parentSalesOrderLineNumber: parent.salesOrderLineNumber,
          bomLevel: parent.bomLevel + 1,
        };
        if (node.FinishedGoodLineItemId) {
          componentMap.set(node.FinishedGoodLineItemId, numbering);
        }
        if (node.children?.length) {
          walk(node.children as vcFGComponent[], numbering);
        }
      }
    };

    walk(roots, root);

    return { root, componentMap };
  }

  private assignBomLineNumbersForStructuralEdit(
    components: vcFGComponent[],
    originalFg: any,
    correlation?: ScopedComponentCorrelation,
  ): Map<string, BomLineNumbering> {
    correlation ??= this.resolver.correlateCpqAndVasComponents(
      originalFg?.kitComponents,
      components,
    );
    const cpqComponents = originalFg?.kitComponents ?? [];

    const toNumber = (val: any): number | null => {
      if (val == null) return null;
      const n = Number(val);
      return isNaN(n) ? null : n;
    };

    const knownNumbers = [
      toNumber(originalFg?.salesOrderLineNumber),
      ...cpqComponents.map((c: any) => toNumber(c.salesOrderLineNumber)),
    ].filter((n: number | null): n is number => n !== null);
    let nextFreshNumber = knownNumbers.length ? Math.max(...knownNumbers) + 1 : 1;

    // Own salesOrderLineNumber/saleOrderLineRefNumber per node, used only to resolve a
    // child's parent-pointer fields during the walk — not part of the returned map.
    const ROOT_ID = '__fg_root__';
    const numberByLineItemId = new Map<string, number>([
      [ROOT_ID, toNumber(originalFg?.salesOrderLineNumber) ?? 0],
    ]);
    const refByLineItemId = new Map<string, string | null>([
      [ROOT_ID, originalFg?.saleOrderLineRefNumber ?? null],
    ]);

    // Only brand-new (unmatched) rows are returned — matched rows are echoed verbatim
    // from the raw CPQ payload elsewhere and don't need their numbering recomputed.
    const result = new Map<string, BomLineNumbering>();

    // buildNestedLineItems clones each component (`{ ...item, children: [] }`), so `node` below
    // is not the same object reference correlation was built from — resolve back to the
    // original object by GUID before querying the reference-keyed map.
    const originalById = new Map<string, vcFGComponent>(
      components.filter((c) => c.FinishedGoodLineItemId).map((c) => [c.FinishedGoodLineItemId!, c]),
    );
    // Same reasoning as assignBomLineNumbers: staged components are excluded from
    // buildNestedLineItems's tree, so append them as flat roots. correlateCpqAndVasComponents
    // is already stage-aware (see pickCpqMatch in vc-resolver.ts), so cpqMatch lookups below
    // resolve correctly for them exactly as for any other root-level component.
    const stagedComponents = components
      .filter((c) => c.StageGroupNumber)
      .sort((a, b) => Number(a.LineNumber ?? 0) - Number(b.LineNumber ?? 0));
    const roots = [...this.buildNestedLineItems(components), ...stagedComponents];

    const walk = (nodes: vcFGComponent[], parentId: string, parentLevel: number) => {
      for (const node of nodes) {
        const id = node.FinishedGoodLineItemId ?? '';
        const originalNode = originalById.get(id);
        const cpqMatch = originalNode
          ? correlation.vasIdToCpqComponent.get(originalNode)
          : undefined;
        const parentNumber = numberByLineItemId.get(parentId) ?? null;
        const parentRef = refByLineItemId.get(parentId) ?? null;
        const bomLevel = parentLevel + 1;

        if (cpqMatch) {
          const ownNumber = toNumber(cpqMatch.salesOrderLineNumber) ?? 0;
          if (id) {
            numberByLineItemId.set(id, ownNumber);
            refByLineItemId.set(id, cpqMatch.saleOrderLineRefNumber ?? null);
          }
        } else {
          const ownNumber = nextFreshNumber++;
          if (id) {
            numberByLineItemId.set(id, ownNumber);
            refByLineItemId.set(id, null);
          }
          result.set(id, {
            salesOrderLineNumber: ownNumber,
            parentSalesOrderLineNumber: parentNumber,
            bomLevel,
            parentSaleOrderLineRefNumber: parentRef,
          });
        }

        if (node.children?.length) {
          walk(node.children as vcFGComponent[], id, bomLevel);
        }
      }
    };

    walk(roots, ROOT_ID, 0);

    return result;
  }
}
